import { useState } from 'react';
import { api } from '../lib/api';

export default function PipelinePage() {
  const [jobSpecText, setJobSpecText] = useState('');
  const [parsedSpec, setParsedSpec] = useState<unknown>(null);
  const [applicationId, setApplicationId] = useState<string | null>(null);
  const [matchReport, setMatchReport] = useState<unknown>(null);
  const [cvContent, setCvContent] = useState<string | null>(null);
  const [loading, setLoading] = useState('');

  async function handleParse() {
    setLoading('parsing');
    try {
      const result = await api.post<{ parsed_spec: unknown }>('/api/pipeline/ingest', { text: jobSpecText });
      setParsedSpec(result.parsed_spec);
    } catch (err) { alert((err as Error).message); }
    setLoading('');
  }

  async function handleConfirm() {
    setLoading('confirming');
    try {
      const result = await api.post<{ application_id: string }>('/api/pipeline/confirm', { parsed_spec: parsedSpec });
      setApplicationId(result.application_id);
    } catch (err) { alert((err as Error).message); }
    setLoading('');
  }

  async function handleMatch() {
    if (!applicationId) return;
    setLoading('matching');
    try {
      const result = await api.post('/api/pipeline/match', { application_id: applicationId });
      setMatchReport(result);
    } catch (err) { alert((err as Error).message); }
    setLoading('');
  }

  async function handleGenerateCV() {
    if (!applicationId) return;
    setLoading('generating');
    try {
      const result = await api.post<{ content: string }>('/api/pipeline/generate/cv', {
        application_id: applicationId,
        template: 'clean',
      });
      setCvContent(result.content);
    } catch (err) { alert((err as Error).message); }
    setLoading('');
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Application Pipeline</h1>

      {/* Step 1: Paste job spec */}
      <section className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
        <h2 className="font-medium text-gray-900 mb-2">1. Paste Job Specification</h2>
        <textarea
          value={jobSpecText}
          onChange={(e) => setJobSpecText(e.target.value)}
          placeholder="Paste the job listing text here..."
          className="w-full h-32 p-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          aria-label="Job specification text"
        />
        <button
          onClick={handleParse}
          disabled={!jobSpecText.trim() || loading === 'parsing'}
          className="mt-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
        >
          {loading === 'parsing' ? 'Parsing...' : 'Parse Job Spec'}
        </button>
      </section>

      {/* Step 2: Review parsed spec */}
      {parsedSpec && !applicationId && (
        <section className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
          <h2 className="font-medium text-gray-900 mb-2">2. Review Parsed Specification</h2>
          <pre className="text-xs bg-gray-50 p-3 rounded overflow-auto max-h-60">
            {JSON.stringify(parsedSpec, null, 2)}
          </pre>
          <button
            onClick={handleConfirm}
            disabled={loading === 'confirming'}
            className="mt-3 bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50"
          >
            {loading === 'confirming' ? 'Creating...' : 'Confirm & Create Application'}
          </button>
        </section>
      )}

      {/* Step 3: Match analysis */}
      {applicationId && !matchReport && (
        <section className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
          <h2 className="font-medium text-gray-900 mb-2">3. Run Match Analysis</h2>
          <p className="text-sm text-gray-600 mb-3">Compare this role's requirements against your knowledge base.</p>
          <button
            onClick={handleMatch}
            disabled={loading === 'matching'}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-50"
          >
            {loading === 'matching' ? 'Analysing...' : 'Analyse Match'}
          </button>
        </section>
      )}

      {/* Match report display */}
      {matchReport && (
        <section className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
          <h2 className="font-medium text-gray-900 mb-2">Match Report</h2>
          <pre className="text-xs bg-gray-50 p-3 rounded overflow-auto max-h-60">
            {JSON.stringify(matchReport, null, 2)}
          </pre>
          <button
            onClick={handleGenerateCV}
            disabled={loading === 'generating'}
            className="mt-3 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            {loading === 'generating' ? 'Generating...' : 'Generate CV'}
          </button>
        </section>
      )}

      {/* Generated CV */}
      {cvContent && (
        <section className="bg-white rounded-lg border border-gray-200 p-4">
          <h2 className="font-medium text-gray-900 mb-2">Generated CV</h2>
          <pre className="text-xs bg-gray-50 p-3 rounded overflow-auto max-h-96 whitespace-pre-wrap">
            {cvContent}
          </pre>
        </section>
      )}
    </div>
  );
}
